/* eslint-disable react/prop-types */
import { FC } from "react";
import { ChatCircle, Heart } from "@phosphor-icons/react";
import { REACTIONS } from "src/features/SocialFeedContent/components/ReactionPicker/ReactionPicker";

interface Props {
	reactionsCount: number;
	reactionTypes?: string[];
	commentsCount: number;
	isExpanded: boolean;
	onToggleComments: () => void;
	onOpenReactions?: () => void;
}

/**
 * Read-only engagement summary. Admins can see how many reactions and
 * comments a post received, click through to a drawer listing reactors
 * by type, and toggle the comment thread to read it — but cannot react,
 * reply, or otherwise interact.
 */
const PostEngagementBar: FC<Props> = ({
	reactionsCount,
	reactionTypes = [],
	commentsCount,
	isExpanded,
	onToggleComments,
	onOpenReactions
}) => {
	const distinctReactions = reactionTypes
		.map((value) => REACTIONS.find((r) => r.value === value))
		.filter((r): r is (typeof REACTIONS)[number] => Boolean(r));

	const summaryText =
		reactionsCount === 0
			? null
			: reactionsCount === 1
				? "1 person reacted"
				: `${reactionsCount} people reacted`;

	const ariaLabel = summaryText
		? `${summaryText}. View who reacted.`
		: "No reactions";

	const reactionInner = (
		<>
			{distinctReactions.length > 0 ? (
				<span
					aria-label={`Reaction types: ${distinctReactions
						.map((r) => r.name)
						.join(", ")}`}
					className="flex items-center -space-x-1"
				>
					{distinctReactions.map((r) => (
						<span
							key={r.value}
							title={r.name}
							aria-hidden="true"
							className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-sm shadow-xs ring-1 ring-border"
						>
							{r.emoji}
						</span>
					))}
				</span>
			) : (
				<Heart size={14} weight="duotone" aria-hidden="true" />
			)}
			<span>{summaryText ?? "0"}</span>
		</>
	);

	return (
		<div className="px-4 py-2.5 flex items-center justify-between border-t border-border">
			{reactionsCount > 0 && onOpenReactions ? (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onOpenReactions();
					}}
					aria-label={ariaLabel}
					className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
				>
					{reactionInner}
				</button>
			) : (
				<div
					className="flex items-center gap-1.5 text-[13px] text-muted-foreground"
					aria-label={ariaLabel}
				>
					{reactionInner}
				</div>
			)}

			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onToggleComments();
				}}
				aria-expanded={isExpanded}
				className={[
					"inline-flex items-center gap-1.5 text-[13px] transition-colors",
					isExpanded
						? "text-primary font-medium"
						: "text-muted-foreground hover:text-foreground"
				].join(" ")}
			>
				<ChatCircle size={14} weight="duotone" aria-hidden="true" />
				{commentsCount > 0
					? `${commentsCount} ${commentsCount === 1 ? "comment" : "comments"}`
					: "0 comments"}
			</button>
		</div>
	);
};

export default PostEngagementBar;
